import React, { useMemo, useState } from 'react';
import { buildPulse, concernsOf } from '../lib/activityPulse.js';
import { isoDay } from '../lib/activityGroups.js';

// The top of the admin ledger: the last 24 hours (or 7 days) at a glance
// (owner request 2026-09-25). Four headline numbers, a "needs a look" list
// of concerns in plain sentences, then one row per household — including the
// ones that did nothing — with today against that household's usual range,
// a 14-day (12-week) trend, and its pool balance. The logic is in
// lib/activityPulse.js; this file only draws it.
//
// A household row click hands (householdId, fromDay) to onPick so the ledger
// below narrows to that household and window.

const fmt$ = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  const v = Math.round(Math.abs(n));
  return (n < 0 ? '−' : '') + '$' + v.toLocaleString('en-US');
};
const since = (ms) => new Date(ms).toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' });

// Plain-language comparison with the household's own usual (owner,
// 2026-09-25: the bullet bar was not quick to read). One verdict word or
// ratio, coloured only when it is a concern, and the typical figure under it.
function versusUsual(h, week) {
  const per = week ? 'week' : 'day';
  if (!(h.typical > 0)) return { text: h.amount > 0 ? 'New — no usual yet' : 'No usual yet', tone: 'muted', sub: null };
  const sub = `usual ${per} ≈ ${fmt$(h.typical)}`;
  const quiet = h.flags.some((f) => f.kind === 'quiet');
  if (h.amount === 0) return { text: week ? 'Nothing this week' : 'Nothing today', tone: quiet ? 'amber' : 'muted', sub };
  const r = h.amount / h.typical;
  if (r >= 1.25) return { text: `▲ ${r.toFixed(1)}× usual`, tone: r >= 2 ? 'amber' : 'plain', sub };
  if (r <= 0.8) return { text: `▼ ${Math.round((1 - r) * 100)}% below usual`, tone: r < 0.5 ? 'amber' : 'plain', sub };
  return { text: 'Normal', tone: 'ok', sub };
}

function Versus({ h, week }) {
  const v = versusUsual(h, week);
  return (
    <span className="pulse-cmp">
      <span className={'pulse-vs is-' + v.tone}>{v.text}</span>
      {v.sub ? <span className="pulse-usual">{v.sub}</span> : null}
    </span>
  );
}

function Spark({ values, tone }) {
  const max = Math.max(...values, 1);
  const w = 6;
  return (
    <svg className="pulse-spark" width={values.length * w} height="22" aria-hidden="true">
      {values.map((v, i) => {
        const hgt = v > 0 ? Math.max(2, (v / max) * 20) : 1;
        const last = i === values.length - 1;
        return <rect key={i} x={i * w} y={22 - hgt} width={w - 2} height={hgt} rx="1" className={last ? 'is-now is-' + tone : v > 0 ? 'is-on' : 'is-off'} />;
      })}
    </svg>
  );
}

// enrolled: the investment households (null → whoever appears in the rows).
export function AdminPulse({ rows, byId, pool, enrolled, onPick, now }) {
  const [mode, setMode] = useState('day');
  const at = now || new Date();
  const pulse = useMemo(() => buildPulse({ rows, enrolled, pool, now: at, mode }), [rows, enrolled, pool, mode, at.getTime ? Math.floor(at.getTime() / 60000) : 0]);
  const name = (id) => (byId[id] && byId[id].name) || id;
  const concerns = useMemo(() => concernsOf(pulse, name, fmt$), [pulse, byId]);
  const k = pulse.kpis;
  const week = mode === 'week';
  const vs = k.usualMoved > 0 ? Math.round(((k.moved - k.usualMoved) / k.usualMoved) * 100) : null;
  const fromDay = isoDay(new Date(pulse.startMs));
  const pick = (id) => { if (typeof onPick === 'function') onPick(id, fromDay); };

  return (
    <section className="pulse" aria-label={week ? 'Last 7 days' : 'Last 24 hours'}>
      <div className="pulse-head">
        <div>
          <div className="pulse-title">{week ? 'Last 7 days' : 'Last 24 hours'}</div>
          <div className="pulse-since">since {since(pulse.startMs)} · compared with each investor's {week ? 'previous 4 weeks' : 'previous 28 days'}</div>
        </div>
        <div className="pulse-toggle" role="group" aria-label="Window">
          <button type="button" className={!week ? 'is-on' : ''} aria-pressed={!week} onClick={() => setMode('day')}>24 hours</button>
          <button type="button" className={week ? 'is-on' : ''} aria-pressed={week} onClick={() => setMode('week')}>7 days</button>
        </div>
      </div>

      <div className="pulse-kpis">
        <div className="pulse-kpi">
          <div className="pulse-kpi-lbl">Transacted</div>
          <div className="pulse-kpi-num mono">{fmt$(k.moved)}</div>
          <div className={'pulse-kpi-sub' + (vs !== null && Math.abs(vs) >= 25 ? ' is-warn' : '')}>
            {vs === null ? 'no usual level yet' : `${vs >= 0 ? '▲' : '▼'} ${Math.abs(vs)}% vs a usual ${week ? 'week' : 'day'} (${fmt$(k.usualMoved)})`}
          </div>
        </div>
        <div className="pulse-kpi">
          <div className="pulse-kpi-lbl">Value</div>
          <div className="pulse-kpi-num mono pulse-green">{k.value ? '+' + fmt$(k.value) : '$0'}</div>
          <div className="pulse-kpi-sub">K {fmt$(k.kareem)} · Inv {fmt$(k.investor)}</div>
        </div>
        <div className="pulse-kpi">
          <div className="pulse-kpi-lbl">Investors who transacted</div>
          <div className="pulse-kpi-num">{k.active} <span className="pulse-of">of {k.total}</span></div>
          <div className={'pulse-kpi-sub' + (k.quiet ? ' is-warn' : '')}>{k.quiet ? `${k.quiet} usually active went quiet` : 'nobody unusually quiet'}</div>
        </div>
        <div className="pulse-kpi">
          <div className="pulse-kpi-lbl">Pool</div>
          <div className={'pulse-kpi-num' + (k.belowZero ? ' pulse-red' : '')}>{pool ? <>{k.belowZero} <span className="pulse-of">below zero</span></> : '—'}</div>
          <div className="pulse-kpi-sub">{fmt$(k.funded)} funded {week ? 'this week' : 'in 24h'}</div>
        </div>
      </div>

      {concerns.length > 0 ? (
        <div className="pulse-concerns">
          <div className="pulse-concerns-head">Needs a look · {concerns.length}</div>
          <ul>
            {concerns.map((c, i) => (
              <li key={i}>
                <span className={'pulse-chip is-' + c.level}>{({ pool: 'Pool', poolSoon: 'Pool', spike: 'Above usual', low: 'Below usual', quiet: 'Quiet', waiting: 'Waiting' })[c.kind]}</span>
                {c.id ? <button type="button" className="pulse-link" onClick={() => pick(c.id)}>{c.text}</button> : <span>{c.text}</span>}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="pulse-allclear">All normal — nobody below zero, above or below their usual, or unusually quiet.</div>
      )}

      <div className="pulse-table">
        <div className="pulse-row pulse-thead" aria-hidden="true">
          <span>Investor</span>
          <span className="pulse-r">{week ? '7 days' : '24h'}</span>
          <span>Compared with their usual</span>
          <span>{week ? '12 weeks' : '14 days'}</span>
          <span className="pulse-r">Pool balance</span>
          <span>Flag</span>
        </div>
        {pulse.households.map((h) => {
          const m = byId[h.id] || { name: h.id, initials: '?', color: '#94a3b8' };
          const tone = h.flags.some((f) => f.level === 'red') ? 'red' : h.flags.some((f) => f.kind === 'spike' || f.kind === 'low') ? 'amber' : 'ok';
          return (
            <div
              key={h.id}
              className={'pulse-row' + (h.amount === 0 ? ' is-silent' : '')}
              role="button"
              tabIndex={0}
              title={`Show ${m.name}'s rows ${week ? 'from the last 7 days' : 'from the last 24 hours'}`}
              onClick={() => pick(h.id)}
              onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); pick(h.id); } }}
            >
              <span className="pulse-who">
                <span className="avatar act-avatar" style={{ background: m.color }}>{m.initials}</span>
                <span className="pulse-name">{m.name}</span>
              </span>
              <span className="pulse-r mono pulse-amt">{h.amount > 0 ? fmt$(h.amount) : '—'}</span>
              <Versus h={h} week={week} />
              <Spark values={h.spark} tone={tone} />
              <span className={'pulse-r mono pulse-pool' + (h.pool && h.pool.available < 0 ? ' pulse-red' : '')}>
                {h.pool ? fmt$(h.pool.available) : '—'}
                {h.pool && h.pool.pendingOut > 0 ? <span className="pulse-pending">−{fmt$(h.pool.pendingOut)} pending</span> : null}
              </span>
              <span className="pulse-flags">
                {h.flags.map((f) => <span key={f.kind} className={'pulse-chip is-' + f.level}>{f.label}</span>)}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default AdminPulse;
